import { Sale, SalePurpose } from './Sale'
import { Answer } from './Answer'
import { Bookable } from '@app/domain/Bookable'
import { BookingFeedback } from './BookingFeedback'
import { BookingFeedbackRequest } from './BookingFeedbackRequest'
import { BookingReminder } from './BookingReminder'
import { BookingSource } from './BookingSource'
import {
    activeBookingStatusTypes,
    BookingStatus,
    BookingStatusType,
    CancellationReason,
    statusTypesIncurringCancellationCharges,
} from './BookingStatus'
import { Cancellation } from '@app/domain/Cancellation'
import { Charge } from '@app/domain/Charge'
import { Contact } from './Contact'
import { Deposit } from '@app/domain/Deposit'
import { HasBookingStatus } from '@app/features/bookings/pipes/booking-status-filter.pipe'
import { Helper } from '@app/helpers/utilities/helper'
import { QuestionType } from '@app/domain/Question'
import { Customer } from '@app/domain/Customer'
import { Order } from '@app/domain/Order'
import { BookingDocket } from '@app/domain/BookingDocket'
import { ServiceStatus } from '@app/domain/ServiceStatus'

export class Booking implements HasBookingStatus {
    constructor(
        public id: string,
        public organisationId: string,
        public tableIds: string[],
        public contact: Contact,
        public customer: Customer | null,
        public size: number,
        public start: Date,
        public duration: number,
        public notes: string | null,
        public merchantNotes: string | null,
        public agreedToMarketing: boolean,
        public agreedToTerms: boolean,
        public paymentCustomerId: string | null,
        public source: BookingSource,
        public reminders: BookingReminder[],
        public statusEvents: BookingStatus[],
        public feedbackRequests: BookingFeedbackRequest[],
        public feedback: BookingFeedback[],
        public reasonId: string | null,
        public eventId: string | null,
        public answers: Answer[],
        public cancellation: Cancellation | null,
        public deposit: Deposit | null,
        public sendFeedbackRequest: boolean,
        public requiresWheelchairAccess: boolean,
        public requiresDogFriendly: boolean,
        public requiresPramAccessible: boolean,
        public requiresHighchairFriendly: boolean,
        public lockedToTables: boolean,
        public serviceStatus: ServiceStatus | null,
        public dateVoided: Date | null,
        public order: Order | null,
        public docket: BookingDocket | null,
        public sale: Sale | null = null
    ) { }

    get end(): Date {
        const date = new Date(this.start)
        date.setMinutes(this.start.getMinutes() + this.duration)
        return date
    }

    paymentSummary() {
        const payments = (this.deposit?.payments ?? []).map(payment => ({
            id: payment.id,
            paymentIntentId: payment.paymentIntentId,
            amount: payment.amount,
            amountRefunded: payment.refundedAmount(),
            currencyCode: payment.currencyCode,
            dateCreated: payment.dateCreated,
            descriptions: ['Deposit'],
        }))
        for (const charge of this.cancellation?.charges ?? []) {
            payments.push({
                id: charge.id, paymentIntentId: charge.paymentIntentId, amount: charge.amount,
                amountRefunded: charge.dateRefunded ? charge.amount : 0,
                currencyCode: charge.currencyCode, dateCreated: charge.dateCreated,
                descriptions: [this.cancellation?.isPerCover && charge.partySize > 0
                    ? `Cancellation charge (${charge.partySize} ${charge.partySize === 1 ? 'cover' : 'covers'})` : 'Cancellation charge'],
            })
        }
        const sale = this.sale
        if (sale?.purpose === SalePurpose.BookingOrder && sale.payment?.datePaid && (sale.payment.amount ?? 0) > 0) {
            payments.push({
                id: sale.payment.id, paymentIntentId: sale.payment.capturedTransactionId ?? sale.payment.paymentIntentId,
                amount: sale.payment.amount ?? 0, amountRefunded: sale.payment.amountRefunded,
                currencyCode: sale.order.currencyCode, dateCreated: sale.payment.datePaid,
                descriptions: ['Products'],
            })
        }
        // A combined checkout has separate allocations with the same provider payment reference.
        const combined = new Map<string, typeof payments[number]>()
        for (const payment of payments) {
            const key = `${payment.currencyCode}:${payment.paymentIntentId ?? payment.id}`
            const existing = combined.get(key)
            if (existing) {
                existing.amount += payment.amount
                existing.amountRefunded += payment.amountRefunded
                existing.descriptions.push(...payment.descriptions)
            } else {
                combined.set(key, payment)
            }
        }
        const history = [...combined.values()].sort((a, b) => b.dateCreated.getTime() - a.dateCreated.getTime())
        const amountPaid = history.reduce((total, payment) => total + payment.amount, 0)
        const amountRefunded = history.reduce((total, payment) => total + payment.amountRefunded, 0)
        return {
            payments: history,
            amountPaid,
            amountRefunded,
            balance: amountPaid - amountRefunded,
            currencyCode: this.deposit?.currencyCode ?? sale?.order.currencyCode ?? this.cancellation?.currencyCode ?? 'GBP',
        }
    }

    get pendingCancellationCharge(): Charge | null {
        const status = this.status()
        const isPendingCancellation = status.type === BookingStatusType.PendingPayment
        if (!isPendingCancellation) {
            return null
        }
        return status.cancellationCharge()
    }

    canPayForOrder(): boolean {
        const expiry = this.expiryDate()
        return this.dateVoided === null && this.sale?.purpose === SalePurpose.BookingOrder && !this.sale.payment?.datePaid
            && (this.statusType === BookingStatusType.Booked || this.statusType === BookingStatusType.Requested
                || (this.statusType === BookingStatusType.PendingPayment
                    && (expiry === null || expiry.getTime() > Date.now())))
    }

    get pendingDepositCharge(): Charge | null {
        const status = this.status()
        const isPendingPayment = status.type === BookingStatusType.PendingPayment
        if (!isPendingPayment) {
            return null
        }
        return status.depositCharge()
    }

    canRemoveCancellation(): boolean {
        return this.dateVoided === null
            && this.statusType === BookingStatusType.Booked
            && this.cancellation !== null
            && this.cancellation.charges.length === 0
    }

    get dateCreated(): Date {
        const firstStatus = this.statusEvents[0]
        return new Date(firstStatus.dateTime)
    }

    get statusType(): BookingStatusType {
        return this.status().type
    }

    get firstName(): string | null {
        return this.customer?.firstName ?? this.contact.firstName
    }

    get lastName(): string | null {
        return this.customer?.lastName ?? this.contact.lastName
    }

    get emailAddress(): string | null {
        return this.customer?.emailAddress ?? this.contact.emailAddress
    }

    get phoneNumber(): string | null {
        return this.customer?.phoneNumber ?? this.contact.phoneNumber
    }

    get name(): string {
        return this.customer?.name() ?? this.contact.name
    }

    get firstNameInitialisedName(): string {
        if (this.firstName === null || this.firstName.length === 0) {
            return this.name
        }
        if (this.lastName === null || this.lastName.length === 0) {
            return this.name
        }
        return `${this.firstName.charAt(0)}. ${this.lastName}`
    }

    occupiedEndDate(turnaroundInterval: number): Date {
        const turnaroundMilliseconds = turnaroundInterval * 60 * 1000
        return new Date(this.end.getTime() - turnaroundMilliseconds)
    }

    isInProgressAtDate(date: Date): boolean {
        if (this.start > date) {
            return false
        }
        if (this.end < date) {
            return false
        }
        return true
    }

    occupiesTableBetweenDates(startDate: Date, endDate: Date, onTables?: Bookable[]): boolean {
        if (onTables) {
            if (!this.occupiesAnyTable(onTables)) {
                return false
            }
        }
        return !(this.end <= startDate || this.start >= endDate)
    }

    occupiesAnyTable(tables: Bookable[]): boolean {
        return this.tableIds.some(tableId => {
            return tables.some(table => table.tableIds.includes(tableId))
        })
    }

    occursBetweenDates(startDate: Date, endDate: Date): boolean {
        if (startDate <= this.start && endDate >= this.start) {
            return true
        }
        if (startDate >= this.start && startDate <= this.end) {
            return true
        }
        return false
    }

    isActive(): boolean {
        return activeBookingStatusTypes().includes(this.status().type)
    }

    startsBetweenDates(startDate: Date, endDate: Date): boolean {
        return Helper.startsBetweenDate(this.start, startDate, endDate)
    }

    isWalkIn(): boolean {
        if (this.customer !== null) {
            return false
        }
        return this.source === BookingSource.Merchant && this.contact.name.length === 0
    }

    hasBeenConfirmed(): boolean {
        return this.reminders.some(reminder => reminder.confirmedDateTime !== null)
    }

    hasAcknowledgedEndTime(): boolean {
        const acknowledgeAnswer = this.answers.find(answer => {
            return answer.question.type === QuestionType.AcknowledgeEndTime
        })
        if (!acknowledgeAnswer) {
            return false
        }
        return acknowledgeAnswer.isTicked()
    }

    status(): BookingStatus {
        if (this.statusEvents.length === 0) {
            throw Error('Unknown booking status')
        }
        return this.statusEvents[this.statusEvents.length - 1]
    }

    isRunningLate(currentDateTime: Date): boolean {
        if (this.status().type !== BookingStatusType.Booked) {
            return false
        }
        if (this.status().markedAsRunningLateDate()) {
            return true
        }
        if (this.start > currentDateTime) {
            return false
        }
        if (this.end < currentDateTime) {
            return false
        }
        const minutesToBeLate = 15
        const minutesLate = Math.floor(
            (currentDateTime.getTime() - this.start.getTime()) / 1000 / 60
        )
        return minutesLate >= minutesToBeLate
    }

    updateContact(contact: Contact) {
        this.contact = contact
    }

    updatePartySize(size: number) {
        this.size = size
        if (this.statusType === BookingStatusType.PendingPayment) {
            const depositCharge = this.pendingDepositCharge?.withUpdatedPartySize(size) || null
            const cancellationCharge = this.pendingCancellationCharge?.withUpdatedPartySize(size) || null
            const pendingStatus = this.status()
            pendingStatus.updateDepositChargeMetadata(depositCharge)
            pendingStatus.updateCancellationChargeMetadata(cancellationCharge)
        }
    }

    statusDateTime(statusType: BookingStatusType): Date | null {
        const status = this.statusEvents.find(status => {
            return status.type === statusType
        })
        if (status === undefined) {
            return null
        }
        return new Date(status.dateTime)
    }

    cancellationCutOffDate(): Date | null {
        if (this.cancellation === null) {
            return null
        }
        if (this.cancellation.cutOffHours === null) {
            return null
        }
        return new Date(this.start.getTime() - this.cancellation.cutOffHours * 60 * 60 * 1000)
    }

    activeCancellationChargeForBooking(): Charge | null {
        if (this.cancellation === null) {
            return null
        }
        if (this.cancellation.amountRemaining() <= 0) {
            return null
        }
        // assume we do not partially charge for cancellations
        const charge = this.cancellation.charge()
        if (!this.isCancellationChargeActive()) {
            return null
        }
        return charge
    }

    canChargeCancellationFee(): boolean {
        const wouldCharge = this.activeCancellationChargeForBooking() !== null
        const shouldCharge = this.shouldChargeCancellationFee()
        const couldCharge = this.cancellation?.paymentMethodId !== null
        return wouldCharge && shouldCharge && couldCharge
    }

    shouldChargeCancellationFee(): boolean {
        return statusTypesIncurringCancellationCharges().includes(this.status().type)
    }

    expiryDate(): Date | null {
        const status = this.status()
        if (status.type !== BookingStatusType.PendingPayment) {
            return null
        }
        return status.expiryDate()
    }

    updateCustomer(customer: Customer) {
        this.customer = customer
    }

    expiredCancellationCharge(): Charge | null {
        const status = this.expiredStatus()
        return status?.cancellationCharge() ?? null
    }

    expiredDepositCharge(): Charge | null {
        const status = this.expiredStatus()
        return status?.depositCharge() ?? null
    }

    expiredDate(): Date | null {
        const status = this.expiredStatus()
        return status?.dateTime ?? null
    }

    hasDietaryRestrictions(): boolean {
        return this.answers.some(answer => {
            return answer.question.type === QuestionType.DietaryRestrictions || answer.question.type === QuestionType.DietaryRestrictionsText
        })
    }

    private expiredStatus(): BookingStatus | null {
        const status = this.status()
        if (status.type !== BookingStatusType.Cancelled) {
            return null
        }
        const info = status.cancellationInformation()
        const reason = info?.reason
        if (reason !== CancellationReason.ChargeExpired) {
            return null
        }
        const previousStatus = this.statusEvents[this.statusEvents.length - 2]
        if (previousStatus.type !== BookingStatusType.PendingPayment) {
            return null
        }
        return previousStatus
    }

    private isCancellationChargeActive(): boolean {
        const cutOffDate = this.cancellationCutOffDate()
        if (cutOffDate === null) {
            return false
        }
        const status = this.status()
        const currentStatusWouldCharge = statusTypesIncurringCancellationCharges().includes(status.type)
        let cancelledDate: Date
        if (currentStatusWouldCharge) {
           cancelledDate = status.dateTime
        } else {
            cancelledDate = new Date()
        }
        const isAfterCutOff = cancelledDate >= cutOffDate
        return isAfterCutOff
    }

    getUtmSource(): string | null {
        if (this.statusEvents.length === 0) {
            return null
        }
        return this.statusEvents[0].getUtmSource()
    }

    leadTimeDays(): number {
        const msPerDay = 1000 * 60 * 60 * 24
        const diff = this.start.getTime() - this.dateCreated.getTime()
        if (diff <= 0) {
            return 0
        }
        return Math.floor(diff / msPerDay)
    }
}
